<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { Board } from '@/api';
import { errorMessage, fieldErrors } from '@/api/client';
import BaseModal from '@/components/BaseModal.vue';

const props = defineProps({
  board: { type: Object, default: null },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const isEdit = computed(() => Boolean(props.board));

/* Un solo modello per creazione e modifica. L'originale (`BoardEdit.vue`)
 * aveva due blocchi di markup identici sotto `v-if="!isEdit"` e
 * `v-if="isEdit"`, che pilotavano due modelli separati. */
const form = ref({
  name: props.board?.name ?? '',
  private: props.board?.private ?? false,
});

const errors = ref({});
const generalError = ref('');
const busy = ref(false);

async function submit() {
  if (busy.value) return;
  busy.value = true;
  errors.value = {};
  generalError.value = '';
  try {
    const saved = isEdit.value
      ? await Board.update(props.board.id, form.value)
      : await Board.create(form.value.name.trim(), form.value.private);
    emit('close', saved);
  } catch (error) {
    errors.value = fieldErrors(error);
    generalError.value = errorMessage(error, t('boardSaveFailed'));
  } finally {
    busy.value = false;
  }
}
</script>

<template>
  <BaseModal
    :title="isEdit ? $t('BoardEditTitle') : $t('BoardCreateTitle')"
    @close="emit('close')"
  >
    <form id="board-form" @submit.prevent="submit">
      <label class="field">
        <span class="field__label">{{ $t('nameLabel') }}</span>
        <input
          v-model="form.name"
          class="input"
          :class="{ 'input--invalid': errors.name }"
          type="text"
          maxlength="128"
          autofocus
          required
          :placeholder="$t('boardNamePlaceholder')"
        />
        <span v-if="errors.name" class="field__error">{{ errors.name }}</span>
      </label>

      <label class="check">
        <input v-model="form.private" type="checkbox" />
        <span>{{ $t('isPrivateCheckbox') }}</span>
      </label>

      <p v-if="generalError && !errors.name" class="field__error" role="alert">{{ generalError }}</p>
    </form>

    <template #footer>
      <button class="btn" type="button" @click="emit('close')">{{ $t('cancelButton') }}</button>
      <button class="btn btn--primary" type="submit" form="board-form" :disabled="busy">
        {{ isEdit ? $t('saveChangesButton') : $t('createBoardButton') }}
      </button>
    </template>
  </BaseModal>
</template>

<style scoped>
.check {
  display: flex;
  align-items: center;
  gap: var(--s-2);
  font-size: var(--t-sm);
  cursor: pointer;
}
</style>
